<script setup lang="ts">
import {
  getRewardStatus,
  parseRewardDescription,
  isRewardAppOnlyBlocked,
} from '@/utils/rewards';

type Props = {
  reward: Reward;
  buttonVisible?: boolean;
  statusVisible?: boolean;
  bigCard?: boolean;
};

const {
  reward,
  buttonVisible = true,
  statusVisible = true,
  bigCard = false,
} = defineProps<Props>();

type Emits = {
  (e: 'redeem'): void;
};

const emit = defineEmits<Emits>();

const { showMoneyFormat } = useWallet();
const { getDateTime } = useDateTimeHelper();

const countDownTotalTime = computed(() => {
  const now = new Date();
  const expireDatetime = new Date(reward.expires_at);

  return expireDatetime.getTime() > now.getTime()
    ? (expireDatetime.getTime() - now.getTime()) / 1000
    : 0;
});

const isCountDownFinished = ref<boolean>(false);

const rewardIsExpired = computed<boolean>(() => {
  if (isCountDownFinished.value) {
    return true;
  }

  return new Date(reward.expires_at) < new Date();
});

const isButtonVisible = computed<boolean>(() => {
  if (!buttonVisible || reward.status !== 'available') {
    return false;
  }

  return true;
});

// ENG-799: bônus só resgatável pelo app, aberto fora do app. Amarrado ao
// `isButtonVisible` de propósito: este mesmo componente é reusado DENTRO do
// modal de confirmação (Redeem.vue) com `buttonVisible: false` — lá não há
// botão pra travar nem espaço pra faixa. Expirado ganha de bloqueado.
const isAppOnlyBlocked = computed<boolean>(
  () =>
    isButtonVisible.value &&
    !rewardIsExpired.value &&
    isRewardAppOnlyBlocked(reward),
);

// ENG-799: a faixa abre o cartaz do bônus app-only
// (`components/app-download/BonusModal.vue`), e não o genérico do menu lateral
// (ENG-495). Ele oferece as duas lojas, então não manda quem está no iPhone
// pra Play Store.
const appDownload = useAppDownloadBonusStore();

function onRewardExpired() {
  isCountDownFinished.value = true;
}

function getRewardIcon(reward: Reward) {
  if (reward.type === 'money_real') {
    return 'user/wallet';
  }

  if (reward.type === 'money_bonus') {
    return 'koin';
  }

  if (reward.type === 'money_cashback') {
    return 'user/cashback';
  }

  if (reward.type === 'freespins') {
    return 'cards';
  }

  if (reward.type === 'freebets') {
    return 'futbol';
  }

  return 'user/rewards';
}

const { normalizeThumbnail, getThumbnailPathSet } = useCasino();

const thumbnailPathSet = computed<string>(() => {
  if (!reward.game_image) {
    return '';
  }

  return getThumbnailPathSet(reward.game_image);
});

const thumbnailPath = computed<string>(() => {
  if (!reward.game_image) {
    return '';
  }

  return `${normalizeThumbnail(reward.game_image)}/public`;
});

const extractedValue = computed<string | null>(
  () => parseRewardDescription(reward.player_desc).value,
);

const displayDescription = computed<string>(
  () => parseRewardDescription(reward.player_desc).description,
);

const isCasinoGame = computed<boolean>(() => {
  return !!reward.game_slug_url;
});

const isSportEvent = computed<boolean>(() => {
  return reward.type === 'freebets';
});

const linkTo = computed<string>(() => {
  if (isCasinoGame.value) {
    const slug = reward.game_slug_url
      ? `/games/${reward.game_slug_url.toLowerCase()}`
      : '/';

    return localeLink(slug);
  }

  if (isSportEvent.value) {
    return localeLink(`/sports`);
  }

  return '';
});
</script>

<template>
  <div
    :class="[
      $style.rewardsItem,
      { [$style.bigCard]: bigCard, [$style.appOnly]: isAppOnlyBlocked },
    ]"
  >
    <div :class="$style.rewardsCol">
      <NuxtLink
        v-tooltip.right="`ID: #${reward.id}`"
        :class="$style.rewardsItemImage"
        :to="linkTo"
      >
        <img
          v-if="thumbnailPath"
          :alt="reward.player_desc"
          :src="thumbnailPath"
          :srcset="thumbnailPathSet || undefined"
        />

        <div :class="$style.rewardsItemIcon">
          <NuxtIcon :name="getRewardIcon(reward)" />
        </div>
      </NuxtLink>

      <div :class="$style.rewardsItemInfo">
        <span :class="$style.rewardsItemName">{{ displayDescription }}</span>

        <div :class="$style.rewardsItemValue">
          <span v-if="extractedValue"> Valor: {{ extractedValue }} </span>
          <span v-else-if="reward.amount">
            Valor: {{ showMoneyFormat({ value: reward.amount / 100 }) }}
          </span>

          <span
            v-if="(extractedValue || reward.amount) && reward.quantity"
            :class="$style.rewardsItemSeparator"
          />

          <span v-if="reward.quantity">
            Quantidade: {{ reward.quantity }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="isButtonVisible || statusVisible"
      :class="$style.rewardsCol"
    >
      <div
        v-if="isButtonVisible"
        :class="$style.rewardsItemButtons"
      >
        <div
          :class="[
            $style.rewardsCountdown,
            {
              [$style.isExpired]: rewardIsExpired,
            },
          ]"
        >
          <div
            v-if="!rewardIsExpired"
            :class="$style.countdownTitle"
          >
            Expira em:
          </div>

          <WidgetsCountdown
            v-if="countDownTotalTime && !rewardIsExpired"
            :total-time="countDownTotalTime"
            @timer-finished="onRewardExpired"
          >
            <template #process="{ time }">
              <span :class="$style.countdownTime">
                {{ `${time.hours}h : ${time.minutes}m : ${time.seconds}s` }}
              </span>
            </template>
            <template #finish>
              <span :class="$style.countdownTime">
                {{ $t('rewards.expired') }}
              </span>
            </template>
          </WidgetsCountdown>

          <template v-else>
            <span :class="$style.countdownTime">
              {{ $t('rewards.expired') }}
            </span>
          </template>
        </div>

        <button
          :class="$style.rewardsItemButton"
          :disabled="rewardIsExpired || isAppOnlyBlocked"
          @click="emit('redeem')"
        >
          Resgatar
        </button>
      </div>

      <div
        v-else-if="statusVisible"
        :class="[
          $style.rewardsItemStatus,
          getRewardStatus(reward.status, !!reward.is_app_only).color,
          getRewardStatus(reward.status, !!reward.is_app_only).bg,
          getRewardStatus(reward.status, !!reward.is_app_only).border,
        ]"
      >
        <span :class="$style.rewardsItemStatusText">
          <NuxtIcon
            :class="[
              $style.rewardsItemStatusIcon,
              getRewardStatus(reward.status, !!reward.is_app_only).color,
            ]"
            :name="getRewardStatus(reward.status, !!reward.is_app_only).icon"
          />

          {{ getRewardStatus(reward.status, !!reward.is_app_only).text }}
        </span>

        <span
          v-if="reward.updated_at"
          :class="$style.rewardsItemUpdatedAt"
        >
          {{ getDateTime(reward.updated_at) }}
        </span>
      </div>
    </div>

    <!-- ENG-799: faixa do bônus app-only. Largura inteira no rodapé do card
         porque a copy não cabe na coluna de ações (contagem regressiva +
         botão, ambos `whitespace-nowrap`).
         A faixa inteira é clicável e abre o cartaz "Baixe o APP e resgate o
         seu bônus". -->
    <button
      v-if="isAppOnlyBlocked"
      :class="$style.appOnlyBanner"
      type="button"
      @click="appDownload.open()"
    >
      <span :class="$style.appOnlyBannerText">
        {{ $t('rewards.app_only.badge') }}
      </span>

      <span :class="$style.appOnlyBannerCta">
        <NuxtIcon name="download" />
        {{ $t('rewards.app_only.cta') }}
      </span>
    </button>
  </div>
</template>

<style module lang="postcss">
.rewardsItem {
  /* `flex-wrap` existe só pra faixa app-only (largura inteira) cair numa
     linha própria no desktop; sem faixa não há o que quebrar. */
  @apply flex flex-col md:flex-row flex-wrap items-start md:items-center justify-between gap-3 md:gap-4 p-3 md:p-4 bg-texts/5 border border-solid border-texts/10 rounded-lg;

  /* ENG-799: realce dourado do bônus app-only. Usa o MESMO `--v2-gold` do
     card novo, não o `accent`/`accent-text` da paleta legada: aquele amarelo
     (#ffe800) berra ao lado do azul do botão "Resgatar", e este dourado é um
     acento desta feature, não da paleta de nenhum dos dois design systems.
     O token vive no `:root` (user-v2-tokens.scss, importado pelo main.scss),
     então é global — não é vazamento de escopo do painel V2. */
  &.appOnly {
    @apply bg-v2-gold/10 border-v2-gold/30;
  }

  .appOnlyBanner {
    @apply w-full flex flex-col md:flex-row items-stretch overflow-hidden text-center rounded-md border border-solid border-v2-gold/40;

    .appOnlyBannerText {
      @apply flex-1 flex items-center justify-center py-2 px-2.5 text-2xs font-extrabold tracking-wide text-v2-gold bg-v2-gold/10;
    }

    .appOnlyBannerCta {
      @apply shrink-0 flex items-center justify-center gap-1 py-2 px-3.5 text-2xs font-extrabold text-v2-background bg-v2-gold;
    }
  }

  .rewardsCol {
    @apply flex items-center justify-between gap-4;
  }

  .rewardsItemImage {
    @apply min-w-12 w-12 h-12 rounded-lg overflow-hidden select-none;

    img {
      @apply w-full h-full object-cover;
    }

    .rewardsItemIcon {
      @apply text-2xl text-primary w-full h-full bg-primary/10 flex items-center justify-center border border-solid border-primary/20 rounded-lg;
    }
  }

  .rewardsItemInfo {
    @apply flex flex-col mr-auto;

    .rewardsItemName {
      @apply text-sm text-texts font-semibold;
    }

    .rewardsItemValue {
      @apply text-sm text-texts/70 flex items-center;

      .rewardsItemSeparator {
        @apply w-px h-4 bg-texts/20 mx-2;
      }
    }
  }

  .rewardsItemButtons {
    @apply flex items-stretch gap-2 select-none;

    .rewardsCountdown {
      @apply text-xs leading-none text-texts/70 w-36 bg-texts/10 rounded-md flex flex-col items-center justify-center py-1.5;

      .countdownTitle {
        @apply text-2xs text-texts/50 font-medium;
      }

      .countdownTime {
        @apply text-xs text-texts/70 font-semibold;
      }

      &.isExpired {
        @apply bg-error/20 text-white;
      }
    }

    .rewardsItemButton {
      @apply text-sm bg-button-bg text-button-text px-4 py-2 rounded-md cursor-pointer font-bold;

      &:disabled {
        @apply opacity-50 cursor-not-allowed;
      }
    }
  }

  .rewardsItemStatus {
    @apply flex items-stretch flex-col select-none py-1.5 px-3 rounded-md border border-solid min-w-44;

    .rewardsItemStatusText {
      @apply text-xs flex items-center gap-1 font-semibold;

      .rewardsItemStatusIcon {
        @apply text-xs text-texts/70;
      }
    }

    .rewardsItemUpdatedAt {
      @apply text-2xs font-medium opacity-70;
    }
  }

  &.bigCard {
    @apply flex-col items-center p-6 text-center;

    .rewardsCol {
      @apply flex-col items-center;
    }

    .rewardsItemImage {
      @apply min-w-24 w-24 h-24 rounded-xl;

      .rewardsItemIcon {
        @apply text-4xl rounded-xl;
      }
    }

    .rewardsItemInfo {
      @apply mr-0 -mb-1;

      .rewardsItemName {
        @apply text-base;
      }

      .rewardsItemValue {
        @apply justify-center;
      }
    }
  }
}
</style>
